'use client';

import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { CalendarProvider, CalendarConnectionStatus, CalendarConnectionProps } from './types';
import { getVenueById } from '../../lib/firebase/venues';
import CalendarProviderSelector from './calendar-provider-selector';
import CalendarConnectionStatusComponent from './calendar-connection-status';
import TripleSeatInstructions from './tripleseat-instructions';
import GoogleIcon from '@mui/icons-material/Google';
import { getBackendURL } from '../../lib/util';

// Extend the Window interface to include google
declare global {
    interface Window {
        google?: any;
    }
}

interface CalendarConnectionManagerProps extends CalendarConnectionProps {
    title?: string;
    description?: string;
    onNext?: (data: any) => void;
}

const CalendarConnectionManager: React.FC<CalendarConnectionManagerProps> = ({
    venueId,
    onConnectionChange,
    showSkipOption = true,
    variant = 'onboarding',
    title = "Connect Your Calendar",
    description = "Connect your calendar to automatically sync your venue's availability and prevent double bookings. We'll only block times when you already have events scheduled.",
    onNext
}) => {
    const [selectedProvider, setSelectedProvider] = useState<CalendarProvider | null>(null);
    const [connectionStatus, setConnectionStatus] = useState<CalendarConnectionStatus>({
        isConnected: false,
        isLoading: true,
        connectedCalendars: [],
        error: undefined
    });

    // Google Calendar specific states
    const [isGoogleConnecting, setIsGoogleConnecting] = useState(false);
    const [isGoogleScriptLoaded, setIsGoogleScriptLoaded] = useState(false);

    // Google Calendar constants
    const GOOGLE_CLIENT_ID = '1068798323193-duapt8n8e3vmdgsm5670vftntrsr0psf.apps.googleusercontent.com';
    const GOOGLE_SCOPES = ['https://www.googleapis.com/auth/calendar.readonly'];

    // Check if Google script is loaded
    useEffect(() => {
        if (window.google) {
            setIsGoogleScriptLoaded(true);
        }
    }, []);

    // Google Calendar connection handler
    const handleGoogleCalendarConnect = () => {
        if (!isGoogleScriptLoaded) {
            // Load Google script if not already loaded
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.onload = () => {
                setIsGoogleScriptLoaded(true);
                initializeGoogleClient();
            };
            document.body.appendChild(script);
            return;
        }
        initializeGoogleClient();
    };

    const initializeGoogleClient = () => {
        setIsGoogleConnecting(true);

        const REDIRECT_URI = process.env.NODE_ENV === 'development'
            ? 'https://www.getlitty.com/venue_admin?id=' + venueId
            : `https://www.getlitty.com/venue_admin?id=` + venueId;

        const client = (window as any).google.accounts.oauth2.initCodeClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: GOOGLE_SCOPES.join(' '),
            redirect_uri: REDIRECT_URI,
            access_type: 'offline',
            ux_mode: 'popup',
            callback: async (response) => {
                if (response.code) {
                    try {
                        const result = await fetch(getBackendURL('getGoogleCalendarToken'), {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ code: response.code, venueId: venueId }),
                        });
                        const data = await result.json();
                        if (data.error) {
                            throw new Error(data.error);
                        }

                        // Update connection status
                        const newStatus = {
                            isConnected: true,
                            isLoading: false,
                            connectedCalendars: [],
                            error: undefined
                        };
                        setConnectionStatus(newStatus);
                        onConnectionChange?.(newStatus);

                        // Refresh page to show updated status
                        if (typeof window !== 'undefined') {
                            window.location.reload();
                        }
                    } catch (error) {
                        console.error('Error connecting Google Calendar:', error);
                        setConnectionStatus(prev => ({
                            ...prev,
                            error: 'Failed to connect Google Calendar. Please try again.',
                            isLoading: false
                        }));
                    }
                } else {
                    console.error('No authorization code received');
                    setConnectionStatus(prev => ({
                        ...prev,
                        error: 'Authorization failed. Please try again.',
                        isLoading: false
                    }));
                }
                setIsGoogleConnecting(false);
            },
        });

        client.requestCode();
    };

    // Fetch the latest venue data to check if calendar has been connected
    useEffect(() => {
        if (!venueId) return;

        const checkCalendarConnection = async () => {
            setConnectionStatus(prev => ({ ...prev, isLoading: true }));
            try {
                const venue = await getVenueById(venueId);
                if (venue) {
                    const newStatus: CalendarConnectionStatus = {
                        isConnected: !!venue.HasConnectedACalendarOnce,
                        isLoading: false,
                        connectedCalendars: [], // This would be populated by GoogleCalendarConnect component
                        error: undefined
                    };
                    setConnectionStatus(newStatus);
                    onConnectionChange?.(newStatus);
                }
            } catch (error) {
                console.error('Error checking calendar connection:', error);
                const errorStatus: CalendarConnectionStatus = {
                    isConnected: false,
                    isLoading: false,
                    connectedCalendars: [],
                    error: 'Failed to check calendar connection status'
                };
                setConnectionStatus(errorStatus);
                onConnectionChange?.(errorStatus);
            }
        };

        checkCalendarConnection();
    }, [venueId, onConnectionChange]);

    const handleProviderSelect = (provider: CalendarProvider) => {
        setSelectedProvider(provider);
    };

    const handleBackToSelection = () => {
        setSelectedProvider(null);
    };

    const handleContinue = () => {
        onNext?.({});
    };

    const handleTripleSeatComplete = () => {
        // When Tripleseat instructions are complete, switch to Google Calendar connection
        setSelectedProvider('google');
    };

    const handleConnectionUpdate = (status: CalendarConnectionStatus) => {
        setConnectionStatus(status);
        onConnectionChange?.(status);
    };

    if (connectionStatus.isLoading) {
        return (
            <Box className="py-6 flex justify-center items-center">
                <CircularProgress sx={{ color: '#C0C0DD' }} />
            </Box>
        );
    }

    // Show success state if calendar is connected
    if (connectionStatus.isConnected) {
        return (
            <CalendarConnectionStatusComponent
                status={connectionStatus}
                title={title}
                onContinue={handleContinue}
                onReconfigure={() => setConnectionStatus(prev => ({ ...prev, isConnected: false }))}
                variant={variant}
            />
        );
    }

    return (
        <Box className="py-6">
            <Typography variant="h5" gutterBottom fontFamily="Roboto Condensed" className="text-[#C0C0DD] mb-2">
                {title}
            </Typography>

            <Typography variant="body1" paragraph className="text-gray-300 mb-6">
                {description}
            </Typography>

            {connectionStatus.error && (
                <Box sx={{ mb: 4, p: 2, bgcolor: 'rgba(255, 56, 92, 0.1)', border: '1px solid rgba(255, 56, 92, 0.3)', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: '#ff385c' }}>
                        {connectionStatus.error}
                    </Typography>
                </Box>
            )}

            {!selectedProvider ? (
                <>
                    <CalendarProviderSelector
                        onProviderSelect={handleProviderSelect}
                        selectedProvider={selectedProvider}
                        description=""
                    />

                    {showSkipOption && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5 }}>
                            <Button
                                variant="outlined"
                                onClick={handleContinue}
                                sx={{
                                    color: '#C0C0DD',
                                    borderColor: '#C0C0DD',
                                    '&:hover': {
                                        borderColor: '#E0B1F1',
                                        bgcolor: 'rgba(224, 177, 241, 0.1)'
                                    }
                                }}
                            >
                                Skip for now
                            </Button>
                        </Box>
                    )}
                </>
            ) : selectedProvider === 'google' ? (
                <Box className="text-center bg-gray-900 rounded-xl p-6 border border-[#E0B1F1]/20">
                    <Typography variant="h6" gutterBottom className="mb-6 text-white">
                        Connect your Google Calendar
                    </Typography>

                    <Typography variant="body2" className="mb-6 text-gray-300">
                        We'll only access your calendar to check for existing events and prevent double bookings.
                        Your calendar data remains private and secure.
                    </Typography>

                    <Box className="flex justify-center mb-6">
                        <Button
                            onClick={handleGoogleCalendarConnect}
                            disabled={isGoogleConnecting}
                            startIcon={isGoogleConnecting ? <CircularProgress size={20} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} /> : <GoogleIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />}
                            variant="contained"
                            sx={{
                                backgroundColor: '#C0C0DD',
                                color: 'black',
                                fontFamily: 'Inter',
                                fontSize: '1rem',
                                padding: '14px 20px',
                                borderRadius: '12px',
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: 'none',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    backgroundColor: '#D8A8E8',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                },
                                '&:focus': {
                                    outline: 'none',
                                    boxShadow: '0 0 0 3px rgba(224,177,241,0.5)',
                                },
                                '&:disabled': {
                                    backgroundColor: 'rgba(192, 192, 221, 0.6)',
                                    color: 'rgba(0, 0, 0, 0.6)',
                                }
                            }}
                        >
                            {isGoogleConnecting ? 'Connecting...' : 'Connect Google Calendar'}
                        </Button>
                    </Box>

                    <Button
                        variant="text"
                        sx={{
                            color: '#C0C0DD',
                            fontFamily: 'Inter',
                            fontSize: '0.875rem',
                            marginBottom: 2,
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: 'rgba(224,177,241,0.1)',
                            },
                        }}
                        onClick={() => window.open('mailto:support@getlitty.com?subject=Additional Calendar Connection Request', '_blank')}
                    >
                        Have another type of calendar? Send us an email and we'll set it up for you!
                    </Button>

                    <Button
                        variant="outlined"
                        onClick={handleBackToSelection}
                        sx={{
                            color: '#C0C0DD',
                            borderColor: '#C0C0DD',
                            '&:hover': {
                                borderColor: '#E0B1F1',
                                bgcolor: 'rgba(224, 177, 241, 0.1)'
                            }
                        }}
                    >
                        Back to options
                    </Button>
                </Box>
            ) : selectedProvider === 'tripleseat' ? (
                <Box>
                    <Typography variant="h6" gutterBottom className="mb-4 text-white">
                        Connect Tripleseat to Litty
                    </Typography>

                    <TripleSeatInstructions
                        onComplete={handleTripleSeatComplete}
                        variant="inline"
                    />

                    <Button
                        variant="outlined"
                        onClick={handleBackToSelection}
                        sx={{
                            color: '#C0C0DD',
                            borderColor: '#C0C0DD',
                            mt: 4,
                            '&:hover': {
                                borderColor: '#E0B1F1',
                                bgcolor: 'rgba(224, 177, 241, 0.1)'
                            }
                        }}
                    >
                        Back to options
                    </Button>
                </Box>
            ) : null}

            {selectedProvider && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 6 }}>
                    <Button
                        variant="contained"
                        onClick={handleContinue}
                        sx={{
                            bgcolor: '#C0C0DD',
                            color: '#1C1C34',
                            fontWeight: 600,
                            py: 1.5,
                            px: 4,
                            '&:hover': {
                                bgcolor: '#E0B1F1',
                            },
                        }}
                    >
                        Continue to next step
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default CalendarConnectionManager; 